// Angular modules.
import { Injectable } from '@angular/core';
// Zenklub interfaces.
import { IMonth } from 'app/interfaces/calendar.interface';

// Zenklub Services.
import { DateTimeService } from './datetime.service';

@Injectable()
export class CalendarService {
	// Cached Calendar
	cacheM: any[] = [];
	cacheW: any[] = [];

	// Constructor method.
	constructor(private dateTime: DateTimeService) {}

	// Determine the number of days in a month.
	public daysInMonth(date: Date): number {
		return 32 - new Date(date.getFullYear(), date.getMonth(), 32).getDate();
	}

	// Determine the day of week of a date.
	public dayOfWeek(date: Date): number {
		return date.getDay();
	}

	// Verify if a specific date is today.
	public isToday(date: Date): boolean {
		const today: Date = new Date();
		return date.toDateString() === today.toDateString();
	}

	// Verify if a specific date is past day.
	public isPastDay(date: Date): boolean {
		const today: Date = new Date();
		today.setHours(0, 0, 0, 0);
		date.setHours(0, 0, 0, 0);

		return today > date;
	}

	// Get months list.
	public getMonths(fullName = true): IMonth[] {
		const months: IMonth[] = [];
		const monKeys: string[] = [
			'JAN',
			'FEB',
			'MAR',
			'APR',
			'MAY',
			'JUN',
			'JUL',
			'AUG',
			'SEP',
			'OCT',
			'NOV',
			'DEC',
		];
		const monDefault: string[] = [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
		];

		for (let i = 0; i < monKeys.length; i++) {
			months.push({
				index: i,
				name:
					'CALENDAR.MONTHS.' + monKeys[i] + '.' + (fullName ? 'FULL' : 'ABB') ||
					monDefault[i],
			});
		}

		return months;
	}

	// Get the first day of a week.
	public getFirstDayOfWeek(date: Date): Date {
		const d: Date = new Date(date);
		if (date.getDay() > 0) {
			d.setDate(d.getDate() - d.getDay());
			return d;
		}
		return d;
	}

	// Get last month based on a given date.
	public getLastMonth(date?: Date, getLastDay = false): Date {
		// Current date.
		date = date || new Date();
		const d: Date = new Date(date);
		d.setMonth(d.getMonth() - 1);
		d.setDate(1);

		// Waiting for last day of month?
		if (getLastDay) {
			d.setDate(this.daysInMonth(d));
		}

		// Return last month.
		return d;
	}

	// Get next month based on a given date.
	public getNextMonth(date?: Date, getLastDay = false): Date {
		// Current date.
		date = date || new Date();
		const d: Date = new Date(date);
		d.setMonth(d.getMonth() + 1);
		d.setDate(1);

		// Waiting for last day of month?
		if (getLastDay) {
			d.setDate(this.daysInMonth(d));
		}

		// Return next month.
		return d;
	}

	// Create a grid for a specific month.
	public createCalendarGrid(date: Date): any[] {
		let day = 1;
		let i = 0;
		let j = 0;
		let nMonthDay = 1;
		let haveDays = true;
		let startDay: number = new Date(
			date.getFullYear(),
			date.getMonth(),
			day
		).getDay();

		const dInMonth: number = this.daysInMonth(date);
		const cMonth: number = date.getMonth();
		const cYear: number = date.getFullYear();
		const pDate = this.getLastMonth(date);
		const nDate = this.getNextMonth(date);
		const pMonthDays: number = this.daysInMonth(pDate);

		// Check Cached Month calendar
		if (this.cacheM[cYear]) {
			if (this.cacheM[cYear][cMonth]) {
				return this.cacheM[cYear][cMonth];
			}
			this.cacheM[cYear][cMonth] = [];
		} else {
			this.cacheM[cYear] = [];
			this.cacheM[cYear][cMonth] = [];
		}

		while (haveDays) {
			this.cacheM[cYear][cMonth][i] = [];

			for (j = 0; j < 7; j++) {
				// calculate weeks
				if (j === 0) {
					this.createWeekCalendarGrid(new Date(cYear, cMonth, day));
				}

				if (i === 0) {
					if (j === startDay) {
						const curD = new Date(cYear, cMonth, day);
						this.cacheM[cYear][cMonth][i][j] = {
							day: day++,
							month: cMonth,
							year: cYear,
							offDay: false,
							pastDay: this.isPastDay(curD),
							fullDate: curD.toDateString(),
							today: this.isToday(curD),
						};

						startDay++;
					} else {
						this.cacheM[cYear][cMonth][i][j] = {
							day: pMonthDays - startDay + j + 1,
							month: pDate.getMonth(),
							year: pDate.getFullYear(),
							offDay: true,
							pastDay: true,
							today: false,
							fullDate: new Date(
								pDate.getFullYear(),
								pDate.getMonth(),
								pMonthDays - startDay + j + 1
							).toDateString(),
						};
					}
				} else if (day <= dInMonth) {
					const curD = new Date(cYear, cMonth, day);
					this.cacheM[cYear][cMonth][i][j] = {
						day: day++,
						month: cMonth,
						year: cYear,
						offDay: false,
						pastDay: this.isPastDay(curD),
						fullDate: curD.toDateString(),
						today: this.isToday(curD),
					};
				} else {
					this.cacheM[cYear][cMonth][i][j] = {
						day: nMonthDay++,
						month: nDate.getMonth(),
						year: nDate.getFullYear(),
						offDay: true,
						pastDay: true,
						today: false,
						fullDate: new Date(
							nDate.getFullYear(),
							nDate.getMonth(),
							nMonthDay - 1
						).toDateString(),
					};
					haveDays = false;
				}
				if (day > dInMonth) {
					haveDays = false;
				}
			}
			i++;
		}

		return this.cacheM[cYear][cMonth];
	}

	// Create a calendar grid for a specifi week.
	public createWeekCalendarGrid(date: Date): any {
		const cMonth: number = date.getMonth();
		const cYear: number = date.getFullYear();
		const prev: number = new Date(date.getFullYear(), 0, 1).getTime();
		const week = Math.ceil((date.getTime() - prev + 1) / (86400000 * 7));

		this.cacheW[cYear] = [];
		this.cacheW[cYear][week] = {};

		// Get first day of this week.
		const firstDay: Date = this.getFirstDayOfWeek(date);

		// Last day added.
		let lastAdded: Date = new Date(firstDay);
		const arDays: any[] = [];
		const hours: any[] = [
			0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
			21, 22, 23,
		];

		for (let i = 0; i < 7; i++) {
			// Append this day.
			arDays.push({
				day: lastAdded.getDate(),
				month: lastAdded.getMonth(),
				year: lastAdded.getFullYear(),
				offDay: lastAdded.getMonth() !== cMonth,
				today: this.isToday(lastAdded),
				hours: hours,
				date: new Date(lastAdded),
			});

			// Increment day.
			if (i < 6) {
				lastAdded = new Date(lastAdded.setDate(lastAdded.getDate() + 1));
			}
		}

		this.cacheW[cYear][week] = {
			firstDay: firstDay,
			lastDay: lastAdded,
			arDays: arDays,
		};

		return this.cacheW[cYear][week];
	}

	// Change te name of active week.
	public async buildWeekName(firstDay: Date, lastDay: Date) {
		// Holds week name.
		let weekName: string;

		// First day, month and year.
		const fDay: number = firstDay.getDate();
		const fMonth: number = firstDay.getMonth();
		const fYear: number = firstDay.getFullYear();

		// Last day, month and year.
		const lDay: number = lastDay.getDate();
		const lMonth: number = lastDay.getMonth();
		const lYear: number = lastDay.getFullYear();

		const [ffMonth, llMonth] = await Promise.all([
			this.dateTime.getMonthName(fMonth),
			this.dateTime.getMonthName(lMonth),
		]);

		const firstMonthName: string = ffMonth.FULL;
		const firstMonthNameAbb = ffMonth.ABB;
		const lastMonthNameAbb = llMonth.ABB;

		// First and last year are the same?
		if (fYear === lYear) {
			// First and last month are the same?
			if (fMonth === lMonth) {
				// e.g., 6-12/Aug 2017.
				weekName = fDay + '-' + lDay + '/' + firstMonthName + ' ' + fYear;
			} else {
				// e.g., 30/Jul - 5/Aug 2017.
				weekName =
					fDay +
					'/' +
					firstMonthNameAbb +
					' - ' +
					lDay +
					'/' +
					lastMonthNameAbb +
					' ' +
					lYear;
			}
		} else {
			// e.g., 31/Dec/2017 - 6/Jan/2018.
			weekName =
				fDay +
				'/' +
				firstMonthNameAbb +
				'/' +
				fYear +
				' - ' +
				lDay +
				'/' +
				lastMonthNameAbb +
				'/' +
				lYear;
		}

		// Return the name.
		return weekName;
	}
}
